<template>
  <BaseModal
    :show="show"
    title="Dokument einem Vertrag zuordnen"
    height="60%"
    @close="$emit('close')"
  >
    <ElAlert type="info" class="el-alert--primary" show-icon>
      {{ duzen('Wähle', 'Wählen Sie') }} einen Vertrag aus, der diesem Dokument zugeordnet sein soll, oder ändern Sie die bisherige Zuordnung.
    </ElAlert>
    <p class="text-start mt-5">
      Dokument: <span class="font-semibold"> {{ fileName }} </span>
    </p>
    <ElFormItem
      label="Vertrag"
      for="Vertrag"
      class="mb-5"
      label-position="top"
    >
      <ElSelect
        id="Vertrag"
        v-model="id"
        :loading="loading"
        placeholder="Vertrag zuordnen"
        clearable
      >
        <ElOption
          v-for="option in contractOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        >
          <div
            class="text-sm"
            :class="{'text-muted': option.inactive}"
          >
            {{ option.label }} {{ option.inactive ? '(inaktiv)' : '' }}
          </div>
        </ElOption>
      </ElSelect>
    </ElFormItem>
  </BaseModal>
</template>

<script setup lang="ts">
import { ElAlert, ElFormItem, ElOption, ElSelect } from 'element-plus';
import { captureException } from '@sentry/vue';
import { ref, watch } from 'vue';

import { updateDocument } from '@/api/documents/documentsApi';
import type { Document } from '@/api/documents/types';
import BaseModal from '@/application/components/BaseModal.vue';
import { useDocumentStore } from '@/documents/store/documentStore';
import { resolveRelations } from '@/application/utils/api/resolveRelations';
import type { NestedDocuments } from "@/contracts/types/types";
import { call } from "@/api/lib/integration";
import { useContractStore } from "@/contracts/stores/contractStore";
import { storeToRefs } from "pinia";
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

interface Props {
  document: Document,
  show: boolean;
  fileName: string;
}

const props = defineProps<Props>();

const emit = defineEmits(['close', 'open']);

const loading = ref(false);
const editing = ref(false);
const id = ref<number | null>(props.document.contract?.id ?? null);

const { updateUserDocument } = useDocumentStore();
const { contractOptions } = storeToRefs(useContractStore());
const { duzen } = useBaseDataStore();

const linkDocumentToContract = async (document: Document, contractId: number) => {
  loading.value = true;
  await call(
    updateDocument({
      contract_id: contractId,
      document_id: document.id,
    }),
    (data) => {
      if (document.userDocumentId === null) return;

      const { documents: [updatedDocument] } = resolveRelations(data) as NestedDocuments;
      updateUserDocument(document.userDocumentId, updatedDocument!);
    },
    (error) => {
      captureException(error)
    },
  );
  loading.value = false;
  editing.value = false;
};

watch(id, (contractId) => {
  if (contractId === null) return;
  linkDocumentToContract(props.document, contractId);
  emit('close');
});
</script>
